/**
 * Service API - Wrapper fetch pour les appels API backend
 * Gère l'authentification, les erreurs et le formatage des requêtes/réponses
 */

import { useAuthStore } from '@/stores/auth'

// En dev, VITE_SERVER_URL est vide → URLs relatives (/api/...) relayées par le
// proxy Vite (même origine) : accessible depuis tout appareil du réseau sans
// CORS ni IP en dur. En build (pré-prod/prod), l'URL absolue est injectée.
const API_BASE_URL = (import.meta.env.VITE_SERVER_URL || '').replace(/\/+$/, '')

/**
 * Wrapper fetch avec gestion automatique du token
 */
export async function apiFetch(endpoint, options = {}) {
  const authStore = useAuthStore()
  const token = authStore.session?.access_token
  
  // Construction des headers
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...options.headers,
  }
  
  // Ajout du token d'authentification
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  
  // Construction de l'URL complète
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
  
  // Options de la requête
  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include', // Important: envoie les cookies avec les requêtes
  }
  
  // Stringify du body si nécessaire
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    fetchOptions.body = JSON.stringify(options.body)
  }
  
  try {
    const response = await fetch(url, fetchOptions)
    
    // Gestion des erreurs HTTP
    if (!response.ok) {
      // Token expiré ou invalide
      if (response.status === 401) {
        authStore.clearAuth()
        window.location.href = '/login?error=session_expired'
        throw new Error('Session expirée')
      }
      
      // Récupération du message d'erreur du serveur
      const errorData = await response.json().catch(() => null)
      const errorMessage = errorData?.error || errorData?.message || `Erreur HTTP ${response.status}`
      throw new Error(errorMessage)
    }
    
    return response
  } catch (error) {
    // Log pour le debugging
    console.error(`[API Error] ${endpoint}:`, error)
    throw error
  }
}

/**
 * Méthodes HTTP pratiques
 */
export const api = {
  get: (endpoint, options = {}) => apiFetch(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, body, options = {}) => apiFetch(endpoint, { ...options, method: 'POST', body }),
  put: (endpoint, body, options = {}) => apiFetch(endpoint, { ...options, method: 'PUT', body }),
  patch: (endpoint, body, options = {}) => apiFetch(endpoint, { ...options, method: 'PATCH', body }),
  delete: (endpoint, options = {}) => apiFetch(endpoint, { ...options, method: 'DELETE' }),
}

/**
 * Upload de fichier
 */
export async function uploadFile(endpoint, file, onProgress = null) {
  const authStore = useAuthStore()
  const token = authStore.session?.access_token
  
  const formData = new FormData()
  formData.append('file', file)
  
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      body: formData,
      credentials: 'include', // Important: envoie les cookies avec les requêtes
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        authStore.clearAuth()
        window.location.href = '/login?error=session_expired'
        throw new Error('Session expirée')
      }
      
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.error || `Erreur upload: ${response.status}`)
    }
    
    return response
  } catch (error) {
    console.error(`[Upload Error] ${endpoint}:`, error)
    throw error
  }
}

/**
 * Téléchargement de fichier
 */
export async function downloadFile(endpoint, filename) {
  const response = await apiFetch(endpoint)
  const blob = await response.blob()
  
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
